import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  AcademicCapIcon,
  ClockIcon,
  UserGroupIcon,
  PlayIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  LockClosedIcon,
  StarIcon,
  BookOpenIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon, CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import { Card, CardHeader, Badge, Avatar, InlineSpinner, EmptyState, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { lmsApi } from '@/api'
import { apiErrorMessage, saveBlob } from '@/api/lms'
import type { Course, Module, Lesson } from '@/api/lms'

type CourseWithModules = Course & { modules: Module[] }

export default function CourseDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [course, setCourse] = useState<CourseWithModules | null>(null)
  const [loading, setLoading] = useState(true)
  const [enrolling, setEnrolling] = useState(false)
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set())
  const [showRatingModal, setShowRatingModal] = useState(false)
  const [downloadingCertificate, setDownloadingCertificate] = useState(false)
  const [certificateError, setCertificateError] = useState<string | null>(null)
  const [userRating, setUserRating] = useState(0)
  const [userReview, setUserReview] = useState('')

  useEffect(() => {
    if (slug) {
      fetchCourse()
    }
  }, [slug])

  const fetchCourse = async () => {
    setLoading(true)
    try {
      const data = await lmsApi.getCourse(slug!)
      setCourse(data)
      // Expand first module by default
      if (data.modules.length > 0) {
        setExpandedModules(new Set([data.modules[0].id]))
      }
    } catch (error) {
      console.error('Error fetching course:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEnroll = async () => {
    if (!course) return
    setEnrolling(true)
    try {
      await lmsApi.enroll(course.slug)
      fetchCourse()
    } catch (error) {
      console.error('Error enrolling:', error)
    } finally {
      setEnrolling(false)
    }
  }

  const handleCertificate = async () => {
    if (!course) return
    setDownloadingCertificate(true)
    setCertificateError(null)
    try {
      // Issued automatically at completion; requested here if it is not ready yet
      const certificate = course.user_progress?.certificate?.has_pdf
        ? course.user_progress.certificate
        : await lmsApi.issueCourseCertificate(course.slug)
      const blob = await lmsApi.downloadCertificate(certificate.id)
      saveBlob(blob, `attestato_${course.slug}_${certificate.certificate_number}.pdf`)
    } catch (error) {
      console.error('Error downloading certificate:', error)
      setCertificateError(await apiErrorMessage(error, "Impossibile scaricare l'attestato."))
    } finally {
      setDownloadingCertificate(false)
    }
  }

  const handleUnenroll = async () => {
    if (!course) return
    try {
      await lmsApi.unenroll(course.slug)
      fetchCourse()
    } catch (error) {
      console.error('Error unenrolling:', error)
    }
  }

  const handleRating = async () => {
    if (!course || userRating === 0) return
    try {
      await lmsApi.rateCourse(course.id, userRating, userReview || undefined)
      setShowRatingModal(false)
      fetchCourse()
    } catch (error) {
      console.error('Error rating course:', error)
    }
  }

  const toggleModule = (moduleId: number) => {
    setExpandedModules((prev) => {
      const next = new Set(prev)
      if (next.has(moduleId)) {
        next.delete(moduleId)
      } else {
        next.add(moduleId)
      }
      return next
    })
  }

  const getLessonIcon = (type: Lesson['type']) => {
    const icons: Record<string, typeof PlayIcon> = {
      video: PlayIcon,
      text: DocumentTextIcon,
      quiz: QuestionMarkCircleIcon,
      scorm: AcademicCapIcon,
      pdf: DocumentTextIcon,
      html: DocumentTextIcon,
      presentation: DocumentTextIcon,
      resource: DocumentTextIcon,
    }
    return icons[type] || DocumentTextIcon
  }

  const getLevelBadge = (level: Course['level']) => {
    const config: Record<string, { variant: 'success' | 'warning' | 'danger'; label: string }> = {
      basic: { variant: 'success', label: 'Base' },
      beginner: { variant: 'success', label: 'Base' },
      intermediate: { variant: 'warning', label: 'Intermedio' },
      advanced: { variant: 'danger', label: 'Avanzato' },
    }
    return config[level] || { variant: 'success' as const, label: level || 'N/D' }
  }

  const renderStars = (rating: number, interactive = false) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => {
          const Icon = star <= (interactive ? userRating : Math.round(rating)) ? StarSolidIcon : StarIcon
          return (
            <button
              key={star}
              type="button"
              onClick={interactive ? () => setUserRating(star) : undefined}
              className={interactive ? 'focus:outline-none' : 'cursor-default'}
              disabled={!interactive}
            >
              <Icon className={`h-5 w-5 ${star <= (interactive ? userRating : Math.round(rating)) ? 'text-yellow-400' : 'text-gray-300'}`} />
            </button>
          )
        })}
        {!interactive && rating > 0 && (
          <span className="ml-2 text-sm text-gray-600">({rating.toFixed(1)})</span>
        )}
      </div>
    )
  }

  if (loading) {
    return <InlineSpinner message="Caricamento corso..." />
  }

  if (!course) {
    return (
      <EmptyState
        icon={<AcademicCapIcon className="h-12 w-12" />}
        title="Corso non trovato"
        description="Il corso richiesto non esiste"
        action={{
          label: 'Torna ai corsi',
          onClick: () => navigate('/courses'),
        }}
      />
    )
  }

  const totalLessons = course.modules.reduce((acc, mod) => acc + mod.lessons.length, 0)
  const completedLessons = course.modules.reduce(
    (acc, mod) => acc + mod.lessons.filter((l) => l.is_completed).length,
    0
  )
  const progressPercentage = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/courses" className="hover:text-primary-600">
          Corsi
        </Link>
        <ChevronRightIcon className="h-4 w-4" />
        {course.category && (
          <>
            <Link to={`/courses?category=${course.category.slug}`} className="hover:text-primary-600">
              {course.category.name}
            </Link>
            <ChevronRightIcon className="h-4 w-4" />
          </>
        )}
        <span className="text-gray-900 truncate max-w-xs">{course.title}</span>
      </nav>

      {/* Hero Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            {/* Thumbnail */}
            <div className="relative h-64 -mx-4 -mt-4 mb-6 overflow-hidden rounded-t-lg bg-gray-100">
              {(course.thumbnail_url || course.thumbnail) ? (
                <img
                  src={course.thumbnail_url || course.thumbnail || ''}
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                  <AcademicCapIcon className="h-24 w-24 text-primary-400" />
                </div>
              )}
              <div className="absolute top-4 right-4">
                <Badge variant={getLevelBadge(course.level).variant} size="lg">
                  {getLevelBadge(course.level).label}
                </Badge>
              </div>
            </div>

            {/* Title and Description */}
            <div className="space-y-4">
              {course.category && (
                <Badge size="sm">{course.category.name}</Badge>
              )}
              <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>

              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center">
                  <ClockIcon className="h-5 w-5 mr-1" />
                  {course.duration_hours} ore
                </span>
                <span className="flex items-center">
                  <BookOpenIcon className="h-5 w-5 mr-1" />
                  {course.modules_count} moduli, {course.lessons_count} lezioni
                </span>
                <span className="flex items-center">
                  <UserGroupIcon className="h-5 w-5 mr-1" />
                  {course.enrolled_count} iscritti
                </span>
              </div>

              {course.rating && course.rating > 0 && (
                <div className="flex items-center gap-2">
                  {renderStars(course.rating)}
                </div>
              )}

              {/* Instructor */}
              {course.instructor && (
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <Avatar
                    size="md"
                    name={course.instructor.full_name}
                    src={course.instructor.avatar || undefined}
                  />
                  <div>
                    <p className="text-sm text-gray-500">Docente</p>
                    <p className="font-medium text-gray-900">{course.instructor.full_name}</p>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-2">Descrizione</h3>
                <div
                  className="prose prose-sm max-w-none text-gray-600"
                  dangerouslySetInnerHTML={{ __html: course.description || '' }}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Enrollment Card */}
          <Card>
            {course.is_enrolled ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircleSolidIcon className="h-6 w-6" />
                  <span className="font-semibold">Sei iscritto!</span>
                </div>

                {/* Progress */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Progresso</span>
                    <span className="font-medium">{progressPercentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-primary-600 h-3 rounded-full transition-all"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {completedLessons} di {totalLessons} lezioni completate
                  </p>
                </div>

                <Button
                  className="w-full"
                  onClick={() => {
                    // Find first incomplete lesson
                    for (const mod of course.modules) {
                      const lesson = mod.lessons.find((l) => !l.is_completed)
                      if (lesson) {
                        navigate(`/courses/${slug}/lesson/${lesson.id}`)
                        return
                      }
                    }
                    // All completed, go to first lesson
                    if (course.modules[0]?.lessons[0]) {
                      navigate(`/courses/${slug}/lesson/${course.modules[0].lessons[0].id}`)
                    }
                  }}
                >
                  <PlayIcon className="h-5 w-5 mr-2" />
                  {progressPercentage === 0 ? 'Inizia corso' : progressPercentage === 100 ? 'Rivedi corso' : 'Continua'}
                </Button>

                {progressPercentage === 100 && course.has_certificate &&
                  !!course.user_progress?.missing_profile_data?.length && (
                  <div className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
                    Per ricevere l'attestato completa nel profilo:{' '}
                    <strong>{course.user_progress.missing_profile_data.join(', ')}</strong>.{' '}
                    <Link to="/profile" className="text-primary-700 font-medium hover:underline">
                      Vai al profilo
                    </Link>
                  </div>
                )}
                {progressPercentage === 100 && course.has_certificate && (
                  <Button className="w-full" onClick={handleCertificate} disabled={downloadingCertificate}>
                    <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                    {downloadingCertificate ? 'Preparazione attestato...' : 'Scarica attestato'}
                  </Button>
                )}
                {certificateError && (
                  <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{certificateError}</p>
                )}

                {progressPercentage === 100 && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowRatingModal(true)}
                  >
                    <StarIcon className="h-5 w-5 mr-2" />
                    Lascia una recensione
                  </Button>
                )}

                <button
                  onClick={handleUnenroll}
                  className="w-full text-sm text-gray-500 hover:text-red-600 transition-colors"
                >
                  Annulla iscrizione
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">Inizia subito questo corso</p>
                  <p className="text-2xl font-bold text-gray-900">Gratuito</p>
                </div>
                <Button
                  className="w-full"
                  onClick={handleEnroll}
                  disabled={enrolling}
                >
                  {enrolling ? 'Iscrizione in corso...' : 'Iscriviti ora'}
                </Button>
              </div>
            )}
          </Card>

          {/* Course Info */}
          <Card>
            <CardHeader title="Informazioni corso" />
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Durata</dt>
                <dd className="font-medium text-gray-900">{course.duration_hours} ore</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Moduli</dt>
                <dd className="font-medium text-gray-900">{course.modules_count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Lezioni</dt>
                <dd className="font-medium text-gray-900">{course.lessons_count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Livello</dt>
                <dd>
                  <Badge variant={getLevelBadge(course.level).variant} size="sm">
                    {getLevelBadge(course.level).label}
                  </Badge>
                </dd>
              </div>
              {course.sequential_modules && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Percorso</dt>
                  <dd className="font-medium text-gray-900">Moduli in sequenza</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500">Iscritti</dt>
                <dd className="font-medium text-gray-900">{course.enrolled_count}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Attestato</dt>
                <dd className="font-medium text-gray-900">{course.has_certificate ? 'Sì' : 'No'}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>

      {/* Course Content */}
      <Card>
        <CardHeader
          title="Contenuto del corso"
          subtitle={`${course.modules_count} moduli • ${course.lessons_count} lezioni • ${course.duration_hours}h totali`}
        />
        <div className="space-y-2">
          {course.modules.map((module, moduleIndex) => (
            <div key={module.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Module Header */}
              <button
                onClick={() => toggleModule(module.id)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-semibold">
                    {moduleIndex + 1}
                  </span>
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{module.title}</h3>
                    <p className="text-sm text-gray-500">
                      {module.lessons.length} lezioni • {module.duration_minutes} min
                    </p>
                    {course.is_enrolled && module.is_locked && module.blocking_module && (
                      <p className="flex items-center text-xs text-amber-600 mt-1">
                        <LockClosedIcon className="h-4 w-4 mr-1" />
                        Si sblocca completando il modulo "{module.blocking_module.title}"
                      </p>
                    )}
                  </div>
                </div>
                <ChevronDownIcon
                  className={`h-5 w-5 text-gray-500 transition-transform ${
                    expandedModules.has(module.id) ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Module Lessons */}
              {expandedModules.has(module.id) && (
                <div className="divide-y divide-gray-100">
                  {module.lessons.map((lesson, lessonIndex) => {
                    const LessonIcon = getLessonIcon(lesson.type)
                    // Locked by a video still to be watched (the module itself is open)
                    const lockedByVideo =
                      !module.is_locked && !!lesson.is_locked && !lesson.is_completed
                    const canAccess =
                      course.is_enrolled &&
                      !lockedByVideo &&
                      !(module.is_locked && !lesson.is_completed)

                    return (
                      <div
                        key={lesson.id}
                        className={`flex items-center gap-4 p-4 ${
                          canAccess ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-60'
                        }`}
                        onClick={() => {
                          if (canAccess) {
                            navigate(`/courses/${slug}/lesson/${lesson.id}`)
                          }
                        }}
                      >
                        <div className="flex-shrink-0 w-6 text-center text-sm text-gray-400">
                          {moduleIndex + 1}.{lessonIndex + 1}
                        </div>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          lesson.is_completed
                            ? 'bg-green-100 text-green-600'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {lesson.is_completed ? (
                            <CheckCircleSolidIcon className="h-5 w-5" />
                          ) : (
                            <LessonIcon className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${lesson.is_completed ? 'text-gray-500' : 'text-gray-900'}`}>
                            {lesson.title}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="capitalize">{lesson.type}</span>
                            {lesson.duration_minutes > 0 && (
                              <>
                                <span>•</span>
                                <span>{lesson.duration_minutes} min</span>
                              </>
                            )}
                          </div>
                        </div>
                        {canAccess && lesson.type === 'video' && lesson.require_full_view && !lesson.is_completed && (
                          <Badge variant="warning" size="sm">Visione obbligatoria</Badge>
                        )}
                        {course.is_enrolled && lockedByVideo && (
                          <span
                            className="flex items-center text-xs text-amber-600"
                            title={
                              (lesson.blocking_lessons || []).every((b) => b.type === 'video')
                                ? 'Si sblocca dopo aver visto fino alla fine i video obbligatori'
                                : 'Si sblocca dopo aver completato i contenuti che lo precedono'
                            }
                          >
                            <LockClosedIcon className="h-5 w-5 mr-1" />
                            {(lesson.blocking_lessons || []).every((b) => b.type === 'video')
                              ? 'Si sblocca dopo il video'
                              : 'Si sblocca dopo il contenuto precedente'}
                          </span>
                        )}
                        {!canAccess && !(course.is_enrolled && lockedByVideo) && (
                          <LockClosedIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Rating Modal */}
      <Modal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        title="Valuta questo corso"
      >
        <ModalBody>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-gray-600 mb-4">Come valuteresti questo corso?</p>
              <div className="flex justify-center">
                {renderStars(0, true)}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recensione (opzionale)
              </label>
              <textarea
                value={userReview}
                onChange={(e) => setUserReview(e.target.value)}
                placeholder="Condividi la tua esperienza..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={4}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowRatingModal(false)}>
            Annulla
          </Button>
          <Button onClick={handleRating} disabled={userRating === 0}>
            Invia valutazione
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
